import { FileTree, Loading } from '@/components/ui/daisy';
import { CoursesModelsType, CoursesNode } from '@/utils/trees/TreeCourses';
import {
  MouseEvent,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import { twMerge } from 'tailwind-merge';
import { useTreeCourses } from '@/data/context/TreeCoursesContext';
import {
  buildHrefCourseByTreePath,
  buildHrefCourseLineByTreePath,
  buildHrefCourseModuleByTreePath,
  buildHrefUnitByTreePath,
  buildHrefLessonByTreePath,
} from '@/utils/courses';
import { useCourseSegments } from '@/data/hook/course-line/useCoursesSegments';
import useCourseTreeNavigate from '@/data/hook/course-line/useCourseTreeNavigate';

type BaseItem = {
  id: number | string;
  name: string;
};

export type TreeItemProps<T extends BaseItem = BaseItem> = {
  item: T;
  parentId: string;
};

type ValidModelsType = CoursesModelsType;
type DirProps = {
  onToggle: (open?: boolean) => void;
  open: () => void;
  isOpen: boolean;
  isLoading: boolean;
};

type LeafProps = {
  onToggle?: undefined;
  open?: undefined;
  isOpen?: undefined;
  isLoading?: undefined;
};

type Props = PropsWithChildren<
  {
    objKey: string;
    type: ValidModelsType;
  } & TreeItemProps
> &
  (DirProps | LeafProps);

export default function TreeItem({
  objKey,
  item,
  parentId,
  type,
  children,
  open,
  isLoading,
  onToggle,
  isOpen,
}: Props) {
  const { navigate } = useCourseTreeNavigate();
  const { tree } = useTreeCourses();
  const { currentSegment, segments } = useCourseSegments();
  const linkBuilder = useLinkBuilder(type);
  const isSelected = currentSegment?.key === objKey;

  const node = useMemo(
    () => ({
      id: objKey,
      name: item.name,
      type,
      realId: item.id,
    }),
    [objKey, item.name, type, item.id],
  );

  const parentPath = tree?.current.findPath(parentId) ?? [];

  const href = linkBuilder([...parentPath, node]);

  useEffect(() => {
    if (!tree) return;
    tree.current.upsert({ ...node, href }, parentId);
  }, [tree, parentId, href, node]);

  useEffect(() => {
    const segment = segments.find(segment => segment.key === objKey);
    if (segment) open?.();
  }, [segments, objKey, open]);

  const handleOnToggle = (open?: boolean) => {
    onToggle?.(open);
  };

  const onClick = (e?: MouseEvent<HTMLLIElement>) => {
    e?.preventDefault();
    e?.stopPropagation();
    navigate(href, { treeKey: objKey });
  };

  const icon = isLoading
    ? {
        custom: <Loading size="xs" color="primary" />,
      }
    : undefined;

  return (
    <FileTree.Item
      label={item.name}
      open={isOpen}
      icon={icon}
      onToggle={handleOnToggle}
      onClick={onClick}
      classNames={{
        summary: twMerge(
          'truncate',
          isSelected && 'text-primary font-semibold',
        ),
      }}
    >
      {children}
    </FileTree.Item>
  );
}

const useLinkBuilder = (type: ValidModelsType) => {
  const mapping = useMemo(
    () => ({
      courseLine: buildHrefCourseLineByTreePath,
      module: buildHrefCourseModuleByTreePath,
      course: buildHrefCourseByTreePath,
      unit: buildHrefUnitByTreePath,
      lesson: buildHrefLessonByTreePath,
    }),
    [],
  );

  return useCallback(
    (path: CoursesNode[]) => mapping[type](path),
    [mapping, type],
  );
};
